import {
  Box, Tooltip, Typography, useTheme, keyframes,
} from '@mui/material';
import {
  blue, red, amber, green, purple, pink,
} from '@mui/material/colors';
import { alpha } from '@mui/material/styles';
import CheckIcon from '@mui/icons-material/Check';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import { useState, useEffect, useRef } from 'react';
import { useIsDesktop, useIsSmallScreen } from '../../../common/hooks/useResponsive';
import OptimusButton from '../../../common/library/Button/Button';
import { TestIds } from '../../../assets/testIds';

// How long the celebratory confetti stays on screen after an upgrade.
const CONFETTI_DURATION_MS = 120000; // 2 minutes

const confettiFall = keyframes`
  0% {
    transform: translateY(-20px) translateX(0px) rotate(var(--rotation));
    opacity: 0;
  }
  10% {
    opacity: 0.85;
  }
  100% {
    transform: translateY(40px) translateX(var(--drift)) rotate(calc(var(--rotation) + 360deg));
    opacity: 0;
  }
`;

const CONFETTI_PIECES = [
  {
    top: '4%', left: '8%', color: blue[500], rotation: 25, shape: 'rect',
  },
  {
    top: '12%', left: '22%', color: red[500], rotation: -15, shape: 'rect',
  },
  {
    top: '6%', left: '38%', color: amber[500], rotation: 45, shape: 'diamond',
  },
  {
    top: '15%', left: '52%', color: green[500], rotation: -30, shape: 'rect',
  },
  {
    top: '3%', left: '65%', color: purple[500], rotation: 60, shape: 'rect',
  },
  {
    top: '10%', left: '78%', color: pink[500], rotation: -45, shape: 'diamond',
  },
  {
    top: '5%', left: '90%', color: blue[500], rotation: 20, shape: 'rect',
  },
  {
    top: '18%', left: '12%', color: amber[500], rotation: -60, shape: 'diamond',
  },
  {
    top: '20%', left: '35%', color: pink[500], rotation: 35, shape: 'rect',
  },
  {
    top: '22%', left: '60%', color: green[500], rotation: -20, shape: 'diamond',
  },
  {
    top: '8%', left: '48%', color: red[500], rotation: 50, shape: 'rect',
  },
  {
    top: '16%', left: '85%', color: amber[500], rotation: -35, shape: 'rect',
  },
  {
    top: '25%', left: '5%', color: blue[500], rotation: 70, shape: 'diamond',
  },
  {
    top: '24%', left: '75%', color: purple[500], rotation: -55, shape: 'rect',
  },
  {
    top: '2%', left: '30%', color: green[500], rotation: 15, shape: 'rect',
  },
  {
    top: '19%', left: '92%', color: red[500], rotation: -40, shape: 'diamond',
  },
  {
    top: '7%', left: '55%', color: purple[500], rotation: 30, shape: 'rect',
  },
  {
    top: '14%', left: '42%', color: blue[500], rotation: -70, shape: 'diamond',
  },
  {
    top: '26%', left: '25%', color: pink[500], rotation: 55, shape: 'rect',
  },
  {
    top: '11%', left: '70%', color: amber[500], rotation: -10, shape: 'rect',
  },
];

interface MembershipFeature {
  text: string;
  prefix: 'check' | 'add';
  bold?: boolean;
  noUnderline?: boolean;
  tooltip?: string | null;
}

export interface AccountMembershipsPriceCardProps {
  tier: string;
  price: string;
  priceStrikethrough?: string;
  description: string;
  features: MembershipFeature[];
  ctaLabel: string;
  cardVariant: 'current' | 'highlighted' | 'default';
  onButtonClick?: () => void;
  showConfetti?: boolean;
}

export function AccountMembershipsPriceCard({
  tier,
  price,
  priceStrikethrough,
  description,
  features,
  ctaLabel,
  cardVariant,
  onButtonClick,
  showConfetti = false,
}: AccountMembershipsPriceCardProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const isDesktop = useIsDesktop();
  const isSmallScreen = useIsSmallScreen();
  const brandColor = theme.palette.brand.primary;
  const [displayConfetti, setDisplayConfetti] = useState(showConfetti);
  const confettiTimerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    // Show the confetti and arm a single timer to hide it after the celebration
    // window. The timer is kept in a ref (not torn down on every showConfetti
    // change) so that toggling showConfetti back off — e.g. closing the success
    // modal, which resets the upgrade status — does NOT cancel the in-flight
    // timer and strand the confetti on screen forever. It runs the full
    // duration once, then hides.
    if (showConfetti) {
      setDisplayConfetti(true);
      if (confettiTimerRef.current) {
        clearTimeout(confettiTimerRef.current);
      }
      confettiTimerRef.current = setTimeout(() => {
        setDisplayConfetti(false);
      }, CONFETTI_DURATION_MS);
    }
  }, [showConfetti]);

  // Clear the timer only on unmount, to avoid setting state after unmount.
  useEffect(() => () => {
    if (confettiTimerRef.current) {
      clearTimeout(confettiTimerRef.current);
    }
  }, []);

  // Set the 3 types of card variants:
  // current (user's current plan)
  // highlighted (the plan being promoted for upgrade)
  // default (other plans)
  const isCurrent = cardVariant === 'current';
  const isHighlighted = cardVariant === 'highlighted';
  const isDefault = cardVariant === 'default';

  // Use 'Current Plan' label when card is in current state
  const displayLabel = isCurrent ? t('memberships.currentPlan') : ctaLabel;

  const borderStyle = (isHighlighted || (isCurrent && displayConfetti)) ?
    `2px solid ${brandColor}` :
    `1px solid ${theme.palette.grey[200]}`;

  return (
    <Box
      id='plan-price-card'
      sx={{
        width: '100%',
        height: '100%',
        border: borderStyle,
        borderRadius: theme.spacing(1),
        backgroundColor: isCurrent ?
          theme.palette.grey[50] :
          theme.palette.common.white,
        position: 'relative',
        overflow: 'hidden',
        boxShadow: theme.palette.effects.shadowXs,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        padding: theme.spacing(2.25),
        gap: isDesktop ? 0 : theme.spacing(2),
      }}
    >
      {displayConfetti && (
        <Box
          data-testid={TestIds.accountMembershipsPage.confetti}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '35%',
            pointerEvents: 'none',
            zIndex: 0,
          }}>
          {CONFETTI_PIECES.map((piece, index) => (
            <Box
              key={index}
              sx={{
                position: 'absolute',
                top: piece.top,
                left: piece.left,
                width: piece.shape === 'diamond' ? '6px' : '8px',
                height: piece.shape === 'diamond' ? '6px' : '4px',
                backgroundColor: piece.color,
                borderRadius: piece.shape === 'diamond' ? '1px' : '0.5px',
                '--rotation': `${piece.rotation}deg`,
                '--drift': `${(index % 2 === 0 ? 1 : -1) * (4 + (index % 5) * 2)}px`,
                animation: `${confettiFall} ${2.5 + (index % 4) * 0.5}s ease-in-out ${(index * 0.15)}s infinite`,
              } as Record<string, string>}
            />
          ))}
        </Box>
      )}
      <Box sx={{
        display: 'flex', alignItems: 'baseline', gap: theme.spacing(0.5), position: 'relative', zIndex: 1,
      }}>
        {priceStrikethrough && (
          <Typography
            variant="h4"
            sx={{
              color: alpha(theme.palette.grey[800], 0.35),
              textDecoration: 'line-through',
              textDecorationSkipInk: 'none',
              mr: theme.spacing(0.5),
            }}
          >
            {priceStrikethrough}
          </Typography>
        )}
        <Typography variant="h4" sx={{ color: brandColor }}>
          {price}
        </Typography>
      </Box>

      <Typography variant="headingXl" sx={{ position: 'relative', zIndex: 1 }}>
        {tier}
      </Typography>

      <Typography
        variant="bodyMd"
        sx={{
          color: theme.palette.grey[800],
          minHeight: isDesktop ? theme.spacing(7.5) : undefined,
          position: 'relative',
          zIndex: 1,
        }}>
        {description}
      </Typography>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: isDesktop ? theme.spacing(31.25) : undefined,
          position: 'relative',
          zIndex: 1,
        }}
      >
        {features.map((feature) => {
          // The Tooltip wraps only this Typography (the icon is a sibling
          // outside it), and as a flex item it is sized to its content width,
          // so the tooltip trigger area is the benefit text itself.
          const featureText = (
            <Typography
              variant="bodyMd"
              sx={{
                color: theme.palette.grey[800],
                textDecoration: feature.noUnderline ? 'none' : 'underline',
                textDecorationStyle: feature.noUnderline ? undefined : 'dotted',
                cursor: feature.tooltip ? 'pointer' : 'default',
              }}
            >
              {feature.text}
            </Typography>
          );

          return (
            <Box
              key={feature.text}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                py: theme.spacing(1),
                pl: theme.spacing(1),
              }}
            >
              {feature.prefix === 'check' ? (
                <CheckIcon sx={{
                  color: brandColor,
                  fontSize: theme.spacing(2),
                  mr: 0.5,
                  mt: theme.spacing(0.25),
                  flexShrink: 0,
                }}
                />
              ) : (
                <AddIcon sx={{
                  color: brandColor,
                  fontSize: theme.spacing(2),
                  mr: 0.5,
                  mt: theme.spacing(0.25),
                  flexShrink: 0,
                }}
                />
              )}
              {feature.tooltip ? (
                <Tooltip
                  title={feature.tooltip}
                  enterTouchDelay={0}
                  leaveTouchDelay={3000}
                  arrow
                  placement={isSmallScreen ? 'bottom-start' : 'right'}
                  slotProps={{
                    popper: isSmallScreen ? {
                      modifiers: [{
                        name: 'offset',
                        options: { offset: [0, -9] },
                      }],
                    } : undefined,
                    tooltip: {
                      sx: {
                        backgroundColor: alpha(theme.palette.grey[900], 0.9),
                        color: theme.palette.common.white,
                        fontSize: theme.spacing(1.5),
                        padding: theme.spacing(1),
                        borderRadius: theme.spacing(0.5),
                        maxWidth: theme.spacing(44.375),
                      },
                    },
                    arrow: {
                      sx: {
                        color: alpha(theme.palette.grey[900], 0.9),
                      },
                    },
                  }}
                >
                  {featureText}
                </Tooltip>
              ) : (
                featureText
              )}
            </Box>
          );
        })}
      </Box>

      {isCurrent && (
        <OptimusButton
          disabled
          fullWidth
          size="m"
          customVariant="secondaryGray"
          buttonText={displayLabel}
          data-testid="membership-current-plan-button"
        />
      )}

      {isHighlighted && (
        <OptimusButton
          fullWidth
          size="m"
          customVariant="primary"
          buttonText={displayLabel}
          onClick={onButtonClick}
          data-testid="membership-highlighted-plan-button"
        />
      )}

      {isDefault && (
        <OptimusButton
          fullWidth
          size="m"
          customVariant="secondaryGray"
          buttonText={displayLabel}
          onClick={onButtonClick}
          data-testid="membership-default-plan-button"
        />
      )}
    </Box>
  );
}
